import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import GameShell from '../../shared/ui/GameShell';
import LoadingScreen from '../../shared/ui/LoadingScreen';
import ErrorScreen from '../../shared/ui/ErrorScreen';
import FinishedDaily from '../../shared/ui/FinishedDaily';
import { finish as recordOutcome, isFinished } from '../../shared/dailySession';
import { useDailySnapshot, usePersistedState } from '../../shared/useDailySession';
import DailyDoneOverlay from '../../shared/ui/DailyDoneOverlay';
import { loadTodaysPuzzle, formatDateLabel } from '../../shared/daily';
import {
  DIRECTION_DELTAS,
  parseLevel,
  snapDirection,
  type Cell,
  type PlacedWord,
  type WordSearchLevel,
} from './types';
import './wordSearch.css';

export default function WordSearchGame() {
  const [data, setData] = useState<{
    level: WordSearchLevel;
    dateKey: string;
    isToday: boolean;
  } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadTodaysPuzzle<WordSearchLevel>('wordSearch')
      .then((res) => {
        if (cancelled) return;
        if (!res) setLoadError('No daily puzzle published for Word Search yet.');
        else setData({ level: res.data, dateKey: res.dateKey, isToday: res.isToday });
      })
      .catch((e) => !cancelled && setLoadError(String(e?.message ?? e)));
    return () => {
      cancelled = true;
    };
  }, []);

  if (loadError) return <ErrorScreen message={loadError} gameId="wordSearch" />;
  if (!data) {
    return (
      <GameShell gameId="wordSearch">
        <LoadingScreen label="Fetching today's puzzle…" />
      </GameShell>
    );
  }

  // A daily is one attempt. Once the round has resolved, show the result
  // instead of mounting a fresh board.
  if (isFinished('wordSearch', data.dateKey)) {
    return (
      <FinishedDaily gameId="wordSearch" dateKey={data.dateKey} isToday={data.isToday} />
    );
  }

  return (
    <WordSearchPlay
      key={data.dateKey}
      level={data.level}
      dateKey={data.dateKey}
      isToday={data.isToday}
    />
  );
}

function WordSearchPlay({
  level,
  dateKey,
  isToday,
}: {
  level: WordSearchLevel;
  dateKey: string;
  isToday: boolean;
}) {
  const { grid, words } = useMemo(() => parseLevel(level), [level]);
  const gridSize = level.gridSize;
  const theme = level.theme ?? formatDateLabel(dateKey, isToday);

  const { restored, save } = useDailySnapshot<{
    found: Array<[string, Cell[]]>;
    hintUsed: boolean;
  }>('wordSearch', dateKey);

  const [foundWords, setFoundWords] = useState<Map<string, Cell[]>>(
    () => new Map(restored?.found ?? []),
  );
  const [startCell, setStartCell] = useState<Cell | null>(null);
  const [currentPath, setCurrentPath] = useState<Cell[]>([]);
  const [selectedTarget, setSelectedTarget] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);
  const [hintUsed, setHintUsed] = useState(restored?.hintUsed ?? false);

  usePersistedState(save, { found: [...foundWords], hintUsed }, !completed);

  useEffect(() => {
    if (completed) recordOutcome('wordSearch', dateKey, 'won', { subtitle: 'Found every word.' });
  }, [completed, dateKey]);

  const boardRef = useRef<HTMLDivElement>(null);

  const submitPath = useCallback(
    (path: Cell[]) => {
      if (path.length < 2) return;
      const word = path.map((c) => grid[c.row][c.col]).join('');
      const rev = word.split('').reverse().join('');
      const match = words.find(
        (w) => (w.word === word || w.word === rev) && !foundWords.has(w.word),
      );
      if (match) {
        setFoundWords((prev) => {
          const next = new Map(prev);
          next.set(match.word, path);
          if (next.size === words.length) setCompleted(true);
          return next;
        });
        if (selectedTarget === match.word) setSelectedTarget(null);
      }
    },
    [grid, words, foundWords, selectedTarget],
  );

  const cellAt = useCallback(
    (clientX: number, clientY: number): Cell | null => {
      const el = boardRef.current;
      if (!el) return null;
      const rect = el.getBoundingClientRect();
      const x = clientX - rect.left;
      const y = clientY - rect.top;
      if (x < 0 || y < 0 || x >= rect.width || y >= rect.height) return null;
      const cellW = rect.width / gridSize;
      const col = Math.floor(x / cellW);
      const row = Math.floor(y / cellW);
      if (row < 0 || col < 0 || row >= gridSize || col >= gridSize) return null;
      return { row, col };
    },
    [gridSize],
  );

  const pathFromTo = useCallback(
    (start: Cell, end: Cell): Cell[] => {
      if (start.row === end.row && start.col === end.col) return [start];
      const dir = snapDirection(start.row, start.col, end.row, end.col);
      const { dr, dc } = DIRECTION_DELTAS[dir];
      let steps: number;
      if (dr !== 0 && dc !== 0) {
        steps = Math.max(Math.abs(end.row - start.row), Math.abs(end.col - start.col));
      } else if (dr !== 0) {
        steps = Math.abs(end.row - start.row);
      } else {
        steps = Math.abs(end.col - start.col);
      }
      const path: Cell[] = [];
      for (let i = 0; i <= steps; i++) {
        const r = start.row + i * dr;
        const c = start.col + i * dc;
        if (r < 0 || c < 0 || r >= gridSize || c >= gridSize) break;
        path.push({ row: r, col: c });
      }
      return path;
    },
    [gridSize],
  );

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
    const cell = cellAt(e.clientX, e.clientY);
    if (!cell) return;
    setStartCell(cell);
    setCurrentPath([cell]);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!startCell) return;
    const cell = cellAt(e.clientX, e.clientY);
    if (!cell) return;
    setCurrentPath(pathFromTo(startCell, cell));
  };

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    try {
      (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId);
    } catch {}
    if (currentPath.length >= 2) submitPath(currentPath);
    setStartCell(null);
    setCurrentPath([]);
  };

  const revealWord = useCallback(() => {
    if (hintUsed) return;
    const unfound = words.filter((w) => !foundWords.has(w.word));
    if (unfound.length === 0) return;
    const pick =
      (selectedTarget && unfound.find((w) => w.word === selectedTarget)) ||
      unfound[Math.floor(Math.random() * unfound.length)];
    const { dr, dc } = DIRECTION_DELTAS[pick.direction];
    const path: Cell[] = [];
    for (let i = 0; i < pick.word.length; i++) {
      path.push({ row: pick.startRow + i * dr, col: pick.startCol + i * dc });
    }
    setFoundWords((prev) => {
      const next = new Map(prev);
      next.set(pick.word, path);
      if (next.size === words.length) setCompleted(true);
      return next;
    });
    setHintUsed(true);
    if (selectedTarget === pick.word) setSelectedTarget(null);
  }, [hintUsed, words, foundWords, selectedTarget]);

  const actions = (
    <button
      className="btn btn-secondary"
      onClick={revealWord}
      disabled={completed || hintUsed}
      title="Reveal a word (one per puzzle)"
    >
      ✨ Reveal
    </button>
  );

  const highlightedCells = useMemo(() => {
    const map = new Map<string, 'found' | 'current'>();
    for (const [, path] of foundWords) {
      for (const c of path) map.set(`${c.row},${c.col}`, 'found');
    }
    for (const c of currentPath) {
      if (!map.has(`${c.row},${c.col}`)) map.set(`${c.row},${c.col}`, 'current');
    }
    return map;
  }, [foundWords, currentPath]);

  return (
    <GameShell
      gameId="wordSearch"
      actions={actions}
      subtitle={`${theme} • ${foundWords.size}/${words.length} found`}
    >
      <div className="ws-layout">
        <div
          ref={boardRef}
          className="ws-board"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          style={{ ['--grid-size' as string]: gridSize }}
        >
          <div className="ws-grid">
            {grid.map((row, r) =>
              row.map((ch, c) => {
                const state = highlightedCells.get(`${r},${c}`);
                return (
                  <div
                    key={`${r}-${c}`}
                    className={
                      'ws-cell' +
                      (state === 'found' ? ' is-found' : '') +
                      (state === 'current' ? ' is-current' : '')
                    }
                  >
                    {ch}
                  </div>
                );
              }),
            )}
          </div>
        </div>

        <WordList
          words={words}
          foundWords={foundWords}
          selected={selectedTarget}
          onSelect={(w) => setSelectedTarget((prev) => (prev === w ? null : w))}
        />
      </div>

      {completed && (
        <DailyDoneOverlay
          dateKey={dateKey}
          isToday={isToday}
          subtitle={`Found all ${words.length} words.`}
        />
      )}
    </GameShell>
  );
}

function WordList({
  words,
  foundWords,
  selected,
  onSelect,
}: {
  words: PlacedWord[];
  foundWords: Map<string, unknown>;
  selected: string | null;
  onSelect: (w: string) => void;
}) {
  return (
    <div className="ws-wordlist">
      {words.map((w) => {
        const isFound = foundWords.has(w.word);
        return (
          <button
            key={w.word}
            className={
              'ws-word' +
              (isFound ? ' is-found' : '') +
              (selected === w.word ? ' is-selected' : '')
            }
            onClick={() => !isFound && onSelect(w.word)}
            disabled={isFound}
          >
            {w.word}
          </button>
        );
      })}
    </div>
  );
}
